import { Injectable, computed, inject, signal } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { DEFAULT_ROWS_PER_PAGE, PAGE_SIZE_OPTIONS } from 'src/app/common/constants-ejb';
import {
  containsDangerousContent,
  REGEX_ALPHANUMERIC,
  sanitizeText,
  sanitizeTextarea
} from 'src/app/common/input-utils';
import { ConvenioSeleccionado } from 'src/app/domain/model/convenio/convenio';
import { Oficina } from 'src/app/domain/model/oficina/oficina';
import {
  ActualizarEncabezadoFacturaPayload,
  EstadoFactura,
  ESTADOS_FACTURA_LABELS,
  ESTADOS_FACTURA_SEVERITIES,
  FacturaEncabezado,
  FacturaDetallePrefactura,
  FiltroDetalleFactura,
  FiltroFacturas,
  GuardarDetalleFacturaPayload,
  MESES_OPTIONS,
  ResultadoPaginadoDetalleFactura,
  ResultadoPaginadoFacturas,
  TransporteProveedor
} from 'src/app/domain/model/servicios-entregados/factura-encabezado.model';
import { FacturaEncabezadoRepository } from 'src/app/domain/service/servicios-entregados/factura-encabezado-repository';
import { OficinaRepository } from 'src/app/domain/service/oficina/oficina-repository';
import { AseguradoraSeleccionada } from '../../shared/buscadores/buscador-aseguradora-dialog.component';

@Injectable()
export class ServiciosEntregadosFacade {
  private static readonly ESTADOS_PERMITIDOS_ELIMINAR: ReadonlySet<string> = new Set([
    EstadoFactura.INICIAL,
    EstadoFactura.CALCULADA,
    EstadoFactura.ANULADA
  ]);
  private static readonly ESTADOS_PERMITIDOS_ENVIAR_CLIENTE: ReadonlySet<string> = new Set([
    EstadoFactura.CALCULADA
  ]);
  private static readonly ESTADOS_PERMITIDOS_EDITAR: ReadonlySet<string> = new Set([
    EstadoFactura.INICIAL,
    EstadoFactura.CALCULADA
  ]);
  private static readonly MSG_CONFIRMAR_ELIMINAR =
    'La prefactura será eliminada, ¿Esta seguro de continuar?';
  private static readonly MSG_NO_PERMITE_ELIMINAR =
    'No es posible eliminar la prefactura pues ya se generaron los pedidos para SAP';
  private static readonly MSG_ELIMINACION_EXITOSA = 'El proceso de eliminacion se ha realizado con exito';
  private static readonly MSG_CONFIRMAR_ENVIAR_CLIENTE =
    'La prefactura será enviada a la aseguradora, ¿Esta seguro de continuar?';
  private static readonly MSG_ESTADO_PENDIENTE_APROBAR = 'No se han agregado cumplimientos a la prefactura.';
  private static readonly MSG_ENVIO_CLIENTE_FALLIDO =
    'Error al enviar la factura a la aseguradora. Por favor intente nuevamente.';
  private static readonly MSG_EDITAR_SOLO_PEN_CALC =
    'Solo se puede modificar una factura en estado PENDIENTE o CALCULADA';
  private static readonly MSG_DETALLE_ESTADO_APROBADO =
    'La prefactura se encuentra en estado aprobado y no es posible modificarla';
  private static readonly MSG_DETALLE_ESTADO_ENVIADA_SAP =
    'La prefactura ya fue enviada a SAP';
  private static readonly MSG_DETALLE_ESTADO_ENVIADA_CLIENTE =
    'La prefactura ya fue enviada a la aseguradora';
  private static readonly MSG_DETALLE_ESTADO_NO_PENDIENTE =
    'Solo es posible asociar detalles a una prefactura en estado Pendiente';
  private static readonly MSG_FACTURA_EXISTE =
    'El código ingresado ya se encuentra asociado a otro servicio entregado';
  private static readonly MSG_EDICION_EXITOSA = 'La información se ha guardado con éxito';
  private static readonly MSG_CONFIRMAR_MODIFICAR_DETALLE =
    '¿Esta seguro que desea modificar el detalle?';
  private static readonly MSG_AGREGAR_DETALLE_EN_ESPERA =
    "Es posible que el proceso haya fallado, o que aún se este ejecutando. Por favor espere y verifique en 15 minutos usando la opción 'Ver Detalle' del menú de acciones";
  private static readonly MSG_AGREGAR_DETALLE_EXITOSO = 'El proceso se ha realizado con éxito';
  private static readonly MSG_AGREGAR_DETALLE_FALLIDO =
    'Estimado usuario, en estos momentos estamos presentando fallas técnicas. Por favor contacte al administrador.';
  private static readonly LBL_AGREGAR_DETALLE = 'Agregar detalle';
  private static readonly LBL_MODIFICAR_DETALLE = 'Modificar detalle';
  private static readonly MSG_NOMBRE_FORMATO =
    'Prefactura - El campo alfanumérico no puede contener caracteres especiales';
  private static readonly MSG_OBSERVACIONES_FORMATO =
    'Observaciones - El campo alfanumérico no puede contener caracteres especiales';

  private readonly facturaRepository = inject(FacturaEncabezadoRepository);
  private readonly oficinaRepository = inject(OficinaRepository);
  private readonly messageService = inject(MessageService);
  private readonly confirmationService = inject(ConfirmationService);

  readonly EstadoFactura = EstadoFactura;

  loading = signal(false);
  facturas = signal<FacturaEncabezado[]>([]);
  totalRegistros = signal(0);
  currentPage = signal(1);
  pageSize = signal(DEFAULT_ROWS_PER_PAGE);
  oficinas = signal<Oficina[]>([]);
  clienteCodigoLabel = signal('');
  clienteIdentificacionLabel = signal('');
  clienteNombreLabel = signal('');

  mesesOptions = MESES_OPTIONS;
  pageSizeOptions = PAGE_SIZE_OPTIONS.map(value => ({ label: String(value), value }));
  selectedPageSize = DEFAULT_ROWS_PER_PAGE;

  readonly filtro: FiltroFacturas = {
    incluirAnuladas: false
  };

  totalPages = computed(() => {
    const total = this.totalRegistros();
    const size = this.pageSize();
    return total > 0 ? Math.ceil(total / size) : 0;
  });

  registroInicio = computed(() => {
    if (this.totalRegistros() === 0) {
      return 0;
    }
    const page = this.currentPage();
    const size = this.pageSize();
    return (page - 1) * size + 1;
  });

  registroFin = computed(() => {
    const inicio = this.registroInicio();
    const size = this.pageSize();
    const total = this.totalRegistros();
    const fin = inicio + size - 1;
    return fin > total ? total : fin;
  });

  hasPrevious = computed(() => this.currentPage() > 1);
  hasNext = computed(() => this.currentPage() < this.totalPages());

  async init(): Promise<void> {
    await this.cargarOficinas();
  }

  private async cargarOficinas(): Promise<void> {
    try {
      const data = await this.oficinaRepository.obtenerOficinas();
      this.oficinas.set(data);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Error al cargar las oficinas. Por favor recargue la pagina.'
      });
    }
  }

  buscar(): void {
    const validationErrorSummary = 'Error de Validacion';

    if (this.filtro.cdfactura) {
      const cdFacturaLimpio = this.filtro.cdfactura.trim();
      if (containsDangerousContent(cdFacturaLimpio)) {
        this.messageService.add({
          severity: 'error',
          summary: validationErrorSummary,
          detail: 'El numero de factura contiene caracteres no permitidos.'
        });
        return;
      }
      this.filtro.cdfactura = sanitizeText(cdFacturaLimpio.toUpperCase());
    }

    if (this.filtro.anio) {
      const anioStr = this.filtro.anio.toString().trim();
      if (containsDangerousContent(anioStr)) {
        this.messageService.add({
          severity: 'error',
          summary: validationErrorSummary,
          detail: 'El año contiene caracteres no permitidos.'
        });
        return;
      }

      const anioNum = parseInt(anioStr, 10);
      if (isNaN(anioNum) || anioNum < 1900 || anioNum > 9999) {
        this.messageService.add({
          severity: 'error',
          summary: validationErrorSummary,
          detail: 'El año debe estar entre 1900 y 9999.'
        });
        return;
      }
    }

    this.currentPage.set(1);
    this.cargarFacturas();
  }

  limpiarFiltros(): void {
    this.filtro.cdfactura = undefined;
    this.filtro.mes = undefined;
    this.filtro.anio = undefined;
    this.filtro.nmconsecutivoCliente = undefined;
    this.filtro.numeroIdentificacionCliente = undefined;
    this.filtro.nombreCliente = undefined;
    this.filtro.nmconsecutivoConvenio = undefined;
    this.filtro.nombreConvenio = undefined;
    this.filtro.nmconsecutivoOficina = undefined;
    this.filtro.nombreOficina = undefined;
    this.filtro.incluirAnuladas = false;

    this.clienteCodigoLabel.set('');
    this.clienteIdentificacionLabel.set('');
    this.clienteNombreLabel.set('');
    this.currentPage.set(1);
    this.facturas.set([]);
    this.totalRegistros.set(0);
  }

  onCdFacturaInput(): void {
    if (!this.filtro.cdfactura) {
      return;
    }
    this.filtro.cdfactura = sanitizeText(this.filtro.cdfactura).toUpperCase();
  }

  onAnioInput(): void {
    if (this.filtro.anio === undefined || this.filtro.anio === null) {
      this.filtro.anio = undefined;
      return;
    }

    const anioLimpio = sanitizeText(this.filtro.anio.toString()).replace(/\D/g, '').slice(0, 4);
    this.filtro.anio = anioLimpio ? parseInt(anioLimpio, 10) : undefined;
  }

  onAseguradoraSeleccionada(aseguradora: AseguradoraSeleccionada): void {
    this.filtro.nmconsecutivoCliente = aseguradora.nmconsecutivo;
    this.filtro.numeroIdentificacionCliente = aseguradora.numeroIdentificacion;
    this.filtro.nombreCliente = aseguradora.nombreComercial;

    this.clienteCodigoLabel.set(aseguradora.nmconsecutivo.toString());
    this.clienteIdentificacionLabel.set(aseguradora.numeroIdentificacion || '');
    this.clienteNombreLabel.set(aseguradora.nombreComercial || '');
    this.limpiarConvenio();
  }

  onClienteSeleccionado(aseguradora: AseguradoraSeleccionada): void {
    this.onAseguradoraSeleccionada(aseguradora);
  }

  limpiarCliente(): void {
    this.filtro.nmconsecutivoCliente = undefined;
    this.filtro.numeroIdentificacionCliente = undefined;
    this.filtro.nombreCliente = undefined;
    this.clienteCodigoLabel.set('');
    this.clienteIdentificacionLabel.set('');
    this.clienteNombreLabel.set('');
    this.limpiarConvenio();
  }

  limpiarConvenio(): void {
    this.filtro.nmconsecutivoConvenio = undefined;
    this.filtro.nombreConvenio = undefined;
  }

  onConvenioSeleccionado(convenio: ConvenioSeleccionado): void {
    this.filtro.nmconsecutivoConvenio = convenio.nmconsecutivo;
    this.filtro.nombreConvenio = convenio.dsconvenio;
  }

  private cargarFacturas(): void {
    this.loading.set(true);

    this.facturaRepository
      .listarFacturas(this.filtro, this.currentPage(), this.pageSize())
      .subscribe({
        next: (resultado: ResultadoPaginadoFacturas) => {
          this.facturas.set(resultado.facturas);
          this.totalRegistros.set(resultado.totalRegistros);
          this.loading.set(false);
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error al cargar las facturas. Por favor intente nuevamente.'
          });
          this.loading.set(false);
        }
      });
  }

  irPrimeraPagina(): void {
    if (this.currentPage() > 1) {
      this.currentPage.set(1);
      this.cargarFacturas();
    }
  }

  irPaginaAnterior(): void {
    if (this.hasPrevious()) {
      this.currentPage.update(p => p - 1);
      this.cargarFacturas();
    }
  }

  irPaginaSiguiente(): void {
    if (this.hasNext()) {
      this.currentPage.update(p => p + 1);
      this.cargarFacturas();
    }
  }

  irUltimaPagina(): void {
    const total = this.totalPages();
    if (this.currentPage() < total) {
      this.currentPage.set(total);
      this.cargarFacturas();
    }
  }

  onPageSizeChange(): void {
    this.pageSize.set(this.selectedPageSize);
    this.currentPage.set(1);
    this.cargarFacturas();
  }

  getEstadoLabel(cdestado: EstadoFactura): string {
    return ESTADOS_FACTURA_LABELS[cdestado] || cdestado;
  }

  getEstadoSeverity(cdestado: EstadoFactura) {
    return ESTADOS_FACTURA_SEVERITIES[cdestado] || 'info';
  }

  mostrarAccionEliminar(factura: FacturaEncabezado): boolean {
    return this.puedeEliminarPorEstadoLegacy(factura.cdestado);
  }

  mostrarAccionEnviarCliente(factura: FacturaEncabezado): boolean {
    return this.puedeEnviarClienteSinMensajes(factura);
  }

  mostrarAccionEditar(factura: FacturaEncabezado): boolean {
    return !!factura?.nmconsecutivo;
  }

  mostrarAccionVerDetalle(factura: FacturaEncabezado): boolean {
    const estado = String(factura?.cdestado || '')
      .trim()
      .toUpperCase();
    return !!factura?.nmconsecutivo && estado !== EstadoFactura.INICIAL;
  }

  /**
   * Legacy: {@code ModificarEncabezadoFacturaViewImpl.onClickAgregarDetalle}.
   * El botón existe en estados PEN (Agregar detalle) y CALC (Modificar detalle).
   * Los demás estados mostraban un mensaje en el legacy, aquí ocultamos la
   * acción del menú porque el listado ya distingue el estado.
   */
  mostrarAccionAgregarDetalle(factura: FacturaEncabezado): boolean {
    return !!factura?.nmconsecutivo && this.puedeEditarPorEstado(factura.cdestado);
  }

  /**
   * Legacy: label dinámico del ancla `saAgregarDetalle`.
   * - PEN → "Agregar detalle"
   * - CALC → "Modificar detalle"
   */
  obtenerLabelAgregarDetalle(factura: FacturaEncabezado | undefined): string {
    const estado = String(factura?.cdestado || '').trim().toUpperCase();
    return estado === EstadoFactura.CALCULADA
      ? ServiciosEntregadosFacade.LBL_MODIFICAR_DETALLE
      : ServiciosEntregadosFacade.LBL_AGREGAR_DETALLE;
  }

  puedeGrabarDetalle(cdestado: EstadoFactura | string | undefined): boolean {
    return this.puedeEditarPorEstado(cdestado);
  }

  obtenerMensajeDetalleNoEditable(cdestado: EstadoFactura | string | undefined): string {
    const estado = String(cdestado || '').trim().toUpperCase();
    if (estado === EstadoFactura.ENVIADA_CLIENTE) {
      return ServiciosEntregadosFacade.MSG_DETALLE_ESTADO_ENVIADA_CLIENTE;
    }
    if (estado === EstadoFactura.ENVIADA_SAP) {
      return ServiciosEntregadosFacade.MSG_DETALLE_ESTADO_ENVIADA_SAP;
    }
    if (estado === EstadoFactura.APROBADA) {
      return ServiciosEntregadosFacade.MSG_DETALLE_ESTADO_APROBADO;
    }
    return ServiciosEntregadosFacade.MSG_DETALLE_ESTADO_NO_PENDIENTE;
  }

  async obtenerFacturaParaEditar(nmconsecutivo: number): Promise<FacturaEncabezado | null> {
    this.loading.set(true);
    try {
      return await this.facturaRepository.obtenerFacturaPorConsecutivo(nmconsecutivo);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No fue posible cargar la factura para edición.'
      });
      return null;
    } finally {
      this.loading.set(false);
    }
  }

  async actualizarEncabezado(
    facturaActual: FacturaEncabezado,
    payload: ActualizarEncabezadoFacturaPayload
  ): Promise<boolean> {
    if (!this.puedeEditarPorEstado(facturaActual?.cdestado)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: ServiciosEntregadosFacade.MSG_EDITAR_SOLO_PEN_CALC
      });
      return false;
    }

    const cdfactura = sanitizeText((payload.cdfactura || '').trim().toUpperCase());
    const dsobservaciones = sanitizeTextarea((payload.dsobservaciones || '').trim());

    if (!cdfactura) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'El nombre del encabezado es obligatorio.'
      });
      return false;
    }

    if (cdfactura.length > 12) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'El nombre del encabezado no puede superar 12 caracteres.'
      });
      return false;
    }

    if (!REGEX_ALPHANUMERIC.test(cdfactura)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: ServiciosEntregadosFacade.MSG_NOMBRE_FORMATO
      });
      return false;
    }

    if (dsobservaciones.length > 200) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: 'Las observaciones no pueden superar 200 caracteres.'
      });
      return false;
    }

    if (dsobservaciones && !REGEX_ALPHANUMERIC.test(dsobservaciones)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: ServiciosEntregadosFacade.MSG_OBSERVACIONES_FORMATO
      });
      return false;
    }

    this.loading.set(true);
    try {
      const existe = await this.facturaRepository.existeNombre(cdfactura, facturaActual.nmconsecutivo);
      if (existe) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validacion',
          detail: ServiciosEntregadosFacade.MSG_FACTURA_EXISTE
        });
        return false;
      }

      await this.facturaRepository.actualizarEncabezado(facturaActual.nmconsecutivo, {
        cdfactura,
        dsobservaciones
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Exito',
        detail: ServiciosEntregadosFacade.MSG_EDICION_EXITOSA
      });

      this.cargarFacturas();
      return true;
    } catch (error: any) {
      const detail =
        error?.error?.detail || error?.error?.mensaje || 'No fue posible actualizar la factura.';
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail
      });
      return false;
    } finally {
      this.loading.set(false);
    }
  }

  async validarNombreEnEdicion(cdfactura: string, nmconsecutivo: number): Promise<boolean> {
    const nombre = sanitizeText((cdfactura || '').trim().toUpperCase());
    if (!nombre) {
      return false;
    }

    try {
      const existe = await this.facturaRepository.existeNombre(nombre, nmconsecutivo);
      if (existe) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Validacion',
          detail: ServiciosEntregadosFacade.MSG_FACTURA_EXISTE
        });
        return true;
      }
      return false;
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No fue posible validar el nombre del encabezado.'
      });
      return false;
    }
  }

  enviarFacturaCliente(factura: FacturaEncabezado): void {
    if (!factura || !factura.nmconsecutivo) {
      return;
    }

    if (!this.puedeEnviarCliente(factura)) {
      return;
    }

    this.confirmationService.confirm({
      header: 'Confirmar envío a la aseguradora',
      message: ServiciosEntregadosFacade.MSG_CONFIRMAR_ENVIAR_CLIENTE,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Enviar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-primary',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.ejecutarEnvioCliente(factura);
      }
    });
  }

  eliminarFactura(factura: FacturaEncabezado): void {
    if (!this.puedeEliminarPorEstadoLegacy(factura.cdestado)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: ServiciosEntregadosFacade.MSG_NO_PERMITE_ELIMINAR
      });
      return;
    }

    this.confirmationService.confirm({
      header: 'Confirmar eliminación',
      message: ServiciosEntregadosFacade.MSG_CONFIRMAR_ELIMINAR,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      accept: () => {
        this.ejecutarEliminacion(factura);
      }
    });
  }

  private ejecutarEliminacion(factura: FacturaEncabezado): void {
    this.loading.set(true);
    this.facturaRepository.eliminarFactura(factura.nmconsecutivo).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Exito',
          detail: response?.mensaje || ServiciosEntregadosFacade.MSG_ELIMINACION_EXITOSA
        });
        this.cargarFacturas();
      },
      error: (error) => {
        const detail = error?.error?.detail || 'Error al eliminar la factura. Por favor intente nuevamente.';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail
        });
        this.loading.set(false);
      }
    });
  }

  /**
   * Ejecuta la acción "Agregar detalle" / "Modificar detalle" del legacy.
   *
   * Reglas (legacy ModificarEncabezadoFacturaViewImpl.onClickAgregarDetalle):
   * - PEN → ejecuta directo, sin confirmación.
   * - CALC → pide confirmación "¿Está seguro que desea modificar el detalle?".
   * - ENVC/ENVS/APR/otro → muestra el mensaje de estado correspondiente y no ejecuta.
   *
   * En éxito el estado pasa a CALC y refresca el listado.
   */
  agregarDetalleFactura(factura: FacturaEncabezado | undefined): void {
    if (!factura || !factura.nmconsecutivo) {
      return;
    }

    if (!this.puedeEditarPorEstado(factura.cdestado)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: this.obtenerMensajeDetalleNoEditable(factura.cdestado)
      });
      return;
    }

    const estado = String(factura.cdestado || '').trim().toUpperCase();
    if (estado === EstadoFactura.CALCULADA) {
      this.confirmationService.confirm({
        header: 'Confirmar modificación de detalle',
        message: ServiciosEntregadosFacade.MSG_CONFIRMAR_MODIFICAR_DETALLE,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Continuar',
        rejectLabel: 'Cancelar',
        acceptButtonStyleClass: 'p-button-primary',
        rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
        accept: () => this.ejecutarAgregarDetalle(factura)
      });
      return;
    }

    this.ejecutarAgregarDetalle(factura);
  }

  private ejecutarAgregarDetalle(factura: FacturaEncabezado): void {
    this.loading.set(true);
    this.facturaRepository.agregarDetalle(factura.nmconsecutivo).subscribe({
      next: (response) => {
        if (response && response.exito === false) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Validacion',
            detail: response.mensaje || ServiciosEntregadosFacade.MSG_AGREGAR_DETALLE_EN_ESPERA
          });
          this.loading.set(false);
          return;
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Exito',
          detail: response?.mensaje || ServiciosEntregadosFacade.MSG_AGREGAR_DETALLE_EXITOSO
        });
        this.cargarFacturas();
      },
      error: (error) => {
        const detail =
          error?.error?.detail ||
          error?.error?.mensaje ||
          ServiciosEntregadosFacade.MSG_AGREGAR_DETALLE_FALLIDO;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail
        });
        this.loading.set(false);
      }
    });
  }

  private ejecutarEnvioCliente(factura: FacturaEncabezado): void {
    this.loading.set(true);
    this.facturaRepository.enviarFacturaCliente(factura.nmconsecutivo).subscribe({
      next: (response) => {
        this.messageService.add({
          severity: 'success',
          summary: 'Exito',
          detail: response?.mensaje || 'Factura enviada a la aseguradora'
        });
        this.cargarFacturas();
      },
      error: (error) => {
        const detail = error?.error?.detail || error?.error?.mensaje || ServiciosEntregadosFacade.MSG_ENVIO_CLIENTE_FALLIDO;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail
        });
        this.loading.set(false);
      }
    });
  }

  private puedeEnviarCliente(factura: FacturaEncabezado): boolean {
    if (!this.puedeEnviarClienteSinMensajes(factura)) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Validacion',
        detail: ServiciosEntregadosFacade.MSG_ESTADO_PENDIENTE_APROBAR
      });
      return false;
    }

    return true;
  }

  private puedeEnviarClienteSinMensajes(factura: FacturaEncabezado | undefined): boolean {
    if (!factura) {
      return false;
    }

    const estado = String(factura.cdestado || '').trim().toUpperCase();
    return ServiciosEntregadosFacade.ESTADOS_PERMITIDOS_ENVIAR_CLIENTE.has(estado);
  }

  private puedeEliminarPorEstadoLegacy(cdestado: EstadoFactura | string | undefined): boolean {
    if (!cdestado) {
      return false;
    }
    return ServiciosEntregadosFacade.ESTADOS_PERMITIDOS_ELIMINAR.has(String(cdestado).toUpperCase());
  }

  private puedeEditarPorEstado(cdestado: EstadoFactura | string | undefined): boolean {
    if (!cdestado) {
      return false;
    }
    return ServiciosEntregadosFacade.ESTADOS_PERMITIDOS_EDITAR.has(String(cdestado).toUpperCase());
  }

  cargarDetalleFactura(
    nmconsecutivo: number,
    filtro: FiltroDetalleFactura,
    tamanio: number,
    cursor?: string | null,
    direction: 'NEXT' | 'PREVIOUS' = 'NEXT'
  ): Promise<ResultadoPaginadoDetalleFactura> {
    return firstValueFrom(this.facturaRepository.listarDetalleFactura(nmconsecutivo, filtro, tamanio, cursor, direction));
  }

  listarCatalogoTransporte(): Promise<TransporteProveedor[]> {
    return firstValueFrom(this.facturaRepository.listarCatalogoTransporte());
  }

  guardarDetalleFactura(nmconsecutivo: number, detalles: FacturaDetallePrefactura[]): Promise<void> {
    const payload: GuardarDetalleFacturaPayload = {
      detalles: detalles.map(detalle => ({
        nmconsecutivo: detalle.nmconsecutivo,
        nmconsecutivoCumplimiento: detalle.nmconsecutivoCumplimiento,
        nmcantidadEjecutada: detalle.nmcantidadEjecutada,
        cdmunicipioOrigen: detalle.cdmunicipioOrigen,
        cdmunicipioEntrega: detalle.cdmunicipioEntrega,
        ptvalorTransporte: detalle.ptvalorTransporte,
        ptvalorTransporteAdicional: detalle.ptvalorTransporteAdicional,
        ptvalorAdicionalCons: detalle.ptvalorAdicionalCons,
        ptvalorViaticos: detalle.ptvalorViaticos,
        snpagoViaticos: detalle.snpagoViaticos,
        nmconsecutivoTransporteProveedor: detalle.nmconsecutivoTransporteProveedor,
        dsproveedorTransporte: sanitizeTextarea((detalle.dsproveedorTransporte || '').trim()),
        dsespecialTransporte: sanitizeTextarea((detalle.dsespecialTransporte || '').trim()),
        dsvaletaxiTransporte: sanitizeTextarea((detalle.dsvaletaxiTransporte || '').trim()),
        dsobservacionViaticos: sanitizeTextarea((detalle.dsobservacionViaticos || '').trim())
      }))
    };

    return firstValueFrom(this.facturaRepository.guardarDetalleFactura(nmconsecutivo, payload))
      .then(() => {
        this.messageService.add({
          severity: 'success',
          summary: 'Exito',
          detail: ServiciosEntregadosFacade.MSG_EDICION_EXITOSA
        });
      })
      .catch((error: any) => {
        const detail =
          error?.error?.detail || error?.error?.mensaje || 'No fue posible guardar el detalle de la factura.';
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail
        });
        return Promise.reject(error);
      });
  }
}
